window.BookingHoldTimer = (() => {
  const STORAGE_KEY   = 'bta_hold';
  const TOAST_ID      = 'bta-hold-toast';
  const CART_POLL_MS  = 5000;
  const CART_ATTR_KEY = 'booking_reservation_id';

  // ── Storage ────────────────────────────────────────────────────────────────

  function saveHold(reservationId, expiresAt, cartSnapshot) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ reservationId, expiresAt, cartSnapshot }));
  }

  function loadHold() {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); }
    catch { return null; }
  }

  function clearHold() {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  // ── Post-checkout detection ────────────────────────────────────────────────

  function _isPostCheckout() {
    const path   = window.location.pathname.toLowerCase();
    const search = window.location.search.toLowerCase();
    return (
      path.includes('/thank-you') ||
      path.includes('/thank_you') ||
      path.includes('/orders/') ||
      search.includes('order_id=') ||
      search.includes('checkout_token=')
    );
  }

  // ── Cart snapshot ──────────────────────────────────────────────────────────

  function snapshotCart(reservationId, expiresAt) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        const cartSnapshot = (cart.items || []).map(item => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {}
        }));
        saveHold(reservationId, expiresAt, cartSnapshot);
      })
      .catch(() => {
        saveHold(reservationId, expiresAt, []);
      });
  }

  // ── Cart restore ───────────────────────────────────────────────────────────

  function restoreCart(cartSnapshot, btn) {
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Restoring…';
    }

    const items = cartSnapshot && cartSnapshot.length ? cartSnapshot : null;

    if (!items) {
      window.location.href = '/cart';
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    })
      .then(() => { window.location.href = '/cart'; })
      .catch(() => { window.location.href = '/cart'; });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function formatTime(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  function formatExpiry(isoString) {
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // ── Toast ──────────────────────────────────────────────────────────────────

  function createToast(expiresAt, cartSnapshot) {
    const existing = document.getElementById(TOAST_ID);
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = TOAST_ID;
    toast.className = 'bht-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    // FIX: Both views live in the DOM from the start.
    // We toggle visibility via a data attribute instead of
    // removing / re-appending elements.
    toast.innerHTML = `
      <div class="bht-view bht-main" data-bht-view="main">
        <button class="bht-dismiss" aria-label="Dismiss">&#x2715;</button>
        <div class="bht-label">Slot reserved</div>
        <div class="bht-until">Until ${formatExpiry(expiresAt)}</div>
        <div class="bht-progress-track"><div class="bht-progress-bar"></div></div>
        <div class="bht-timer-row">
          <svg class="bht-icon" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
          </svg>
          <span class="bht-countdown">--:--</span>
        </div>
        <button class="bht-return-btn" type="button">&#8592; Return to basket</button>
      </div>
      <div class="bht-view bht-confirm" data-bht-view="confirm">
        <p class="bht-confirm-msg">Cancel your booking? Your slot will be released and your basket cleared.</p>
        <div class="bht-confirm-actions">
          <button class="bht-confirm-back" type="button">Keep booking</button>
          <button class="bht-confirm-yes"  type="button">Yes, cancel</button>
        </div>
      </div>
    `;

    // Default: show main, hide confirm
    _setView(toast, 'main');

    document.body.appendChild(toast);

    // ── Event listeners ──────────────────────────────────────────────────

    toast.querySelector('.bht-dismiss').addEventListener('click', () => {
      _setView(toast, 'confirm');
    });

    toast.querySelector('.bht-return-btn').addEventListener('click', () => {
      const hold = loadHold();
      restoreCart(hold ? hold.cartSnapshot : null, toast.querySelector('.bht-return-btn'));
    });

    toast.querySelector('.bht-confirm-back').addEventListener('click', () => {
      _setView(toast, 'main');
    });

    toast.querySelector('.bht-confirm-yes').addEventListener('click', () => {
      const yesBtn = toast.querySelector('.bht-confirm-yes');
      if (yesBtn) { yesBtn.disabled = true; yesBtn.textContent = 'Cancelling…'; }
      fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
        .finally(() => {
          _teardown();
          location.reload();
        });
    });

    return toast;
  }

  // FIX: Simple view switcher — no DOM insertion/removal, just display toggling
  function _setView(toast, viewName) {
    toast.querySelectorAll('.bht-view').forEach(el => {
      el.style.display = el.dataset.bhtView === viewName ? '' : 'none';
    });
  }

  function removeToast() {
    const toast = document.getElementById(TOAST_ID);
    if (toast) toast.remove();
  }

  // ── Countdown ──────────────────────────────────────────────────────────────

  let _timerInterval = null;

  function startCountdown(expiresAt) {
    if (_timerInterval) clearInterval(_timerInterval);

    const expiryMs  = new Date(expiresAt).getTime();
    const totalSecs = Math.max(1, Math.round((expiryMs - Date.now()) / 1000));

    function tick() {
      const remaining = Math.max(0, Math.floor((expiryMs - Date.now()) / 1000));

      const toast = document.getElementById(TOAST_ID);
      if (toast) {
        const cd = toast.querySelector('.bht-countdown');
        if (cd) cd.textContent = formatTime(remaining);

        const bar = toast.querySelector('.bht-progress-bar');
        if (bar) bar.style.width = `${(remaining / totalSecs) * 100}%`;

        toast.classList.toggle('bht-expiring', remaining < 180);
      }

      if (remaining <= 0) {
        _teardown();
        fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
          .finally(() => location.reload());
      }
    }

    tick();
    _timerInterval = setInterval(tick, 1000);
  }

  // ── Cart watch ─────────────────────────────────────────────────────────────

  let _cartPollInterval = null;

  function startCartWatch(reservationId) {
    stopCartWatch();

    _cartPollInterval = setInterval(() => {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(r => r.json())
        .then(cart => {
          if (!cart || cart.item_count === 0) {
            _onCartEmpty();
            return;
          }
          const attrs = cart.attributes || {};
          const cartResId = String(attrs[CART_ATTR_KEY] || '').trim();
          if (cartResId && cartResId !== String(reservationId)) {
            _teardown();
            removeToast();
          }
        })
        .catch(() => {});
    }, CART_POLL_MS);
  }

  function stopCartWatch() {
    if (_cartPollInterval) {
      clearInterval(_cartPollInterval);
      _cartPollInterval = null;
    }
  }

function _onCartEmpty() {
    stopCartWatch();

    // If we're on a checkout page and cart is empty, checkout succeeded
    if (window.location.pathname.includes('/checkouts/')) {
      _teardown();
      removeToast();
      return;
    }

    const toast = document.getElementById(TOAST_ID);
    if (toast) toast.classList.add('bht-cart-empty');
  }

  // ── Teardown ───────────────────────────────────────────────────────────────

  function _teardown() {
    if (_timerInterval) { clearInterval(_timerInterval); _timerInterval = null; }
    stopCartWatch();
    clearHold();
  }

  // ── Public API ─────────────────────────────────────────────────────────────


  function startHold(reservationId, expiresAt) {
    saveHold(reservationId, expiresAt, []);
    snapshotCart(reservationId, expiresAt);

    createToast(expiresAt, []);
    startCountdown(expiresAt);
    startCartWatch(reservationId);
  }
function restoreIfActive() {
    if (_isPostCheckout()) {
      clearHold();
      return;
    }

    const hold = loadHold();
    if (!hold) return;

    if (new Date(hold.expiresAt).getTime() - Date.now() <= 0) {
      clearHold();
      return;
    }

    // Check cart before showing toast — if empty, checkout completed
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        if (!cart || cart.item_count === 0) {
          clearHold();
          return;
        }
        createToast(hold.expiresAt, hold.cartSnapshot || []);
        startCountdown(hold.expiresAt);
        startCartWatch(hold.reservationId);
      })
      .catch(function() {
        // Network error — show toast as fallback
        createToast(hold.expiresAt, hold.cartSnapshot || []);
        startCountdown(hold.expiresAt);
        startCartWatch(hold.reservationId);
      });
  }

  return { startHold, restoreIfActive, clearHold };
})();

document.addEventListener('DOMContentLoaded', () => BookingHoldTimer.restoreIfActive());